<template>
  <div class="about-article">
    <Article
      class="about-article__text"
      data-aos="fade-down"
      v-model:content="content"
      :edit-mode="editMode"
    />
    <img
      v-for="(imageNumber, index) in 4"
      :class="[
        'about-article__illustration',
        `about-article__illustration_${imageNumber}`,
      ]"
      :src="finalImages[index]?.src"
      alt="illustration"
      :data-aos="editMode ? undefined : finalImages[index]?.aos"
      :data-aos-delay="editMode ? undefined : index * 100 + 100"
      v-show="finalImages[index]"
    />
    <template v-if="editMode">
      <p
        :class="['about-article__hint', `about-article__hint_${i}`]"
        v-for="i in 4"
      >
        {{ i }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
  import defaultImage1 from "@/assets/images/game-cards/2.webp";
  import defaultImage3 from "@/assets/images/game-cards/3.webp";
  import defaultImage4 from "@/assets/images/game-cards/6.webp";
  import defaultImage2 from "@/assets/images/game-cards/8.webp";

  export interface AboutArticleProps {
    images: (string | null)[];
    editMode?: boolean;
  }

  const props = withDefaults(defineProps<AboutArticleProps>(), {
    images: () => [],
  });

  const content = defineModel<string | null>("content", { required: false });

  const finalImages = computed<
    {
      src: string;
      aos?: string;
      aosDelay?: string;
    }[]
  >(() => {
    return [
      {
        src: props.images?.[0] ?? defaultImage1,
        aos: "fade-right",
      },
      {
        src: props.images?.[1] ?? defaultImage2,
        aos: "fade-right",
      },
      {
        src: props.images?.[2] ?? defaultImage3,
        aos: "fade-left",
      },
      {
        src: props.images?.[3] ?? defaultImage4,
        aos: "fade-right",
      },
    ];
  });
</script>

<style lang="scss">
  .about-article {
    display: flex;
    flex-direction: column;
    gap: 32px;

    padding: 128px 0;

    position: relative;
    isolation: isolate;

    @media (max-width: $lg) {
      padding: 64px 0;
    }

    &_edit-mode {
      .about-article__illustration {
        &::after {
          opacity: 1;
        }
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: 32px;

      min-height: 400px;

      width: calc(100% - 500px);
      margin: 0 auto;

      font-size: 30px !important;

      @media (max-width: $lg) {
        width: 100%;
      }

      a,
      b,
      strong {
        background: none;

        color: #e43233;
      }
    }

    &__illustration {
      position: absolute;

      border-radius: 16px;
      box-shadow: -4px 2px 8px rgba(0, 0, 0, 0.25);

      aspect-ratio: 1;

      object-fit: cover;

      @media (max-width: $lg) {
        position: static;

        transform: none !important;
        width: 100% !important;
      }

      &_1 {
        left: 0;
        top: 64px;

        width: 150px;

        rotate: -5deg;
        z-index: 1;
      }

      &_2 {
        left: 75px;
        top: 32px;

        width: 125px;

        rotate: 5deg;
      }

      &_3 {
        right: 0;
        top: 50%;

        width: 200px;

        rotate: 5deg;
        translate: 0 -50%;
      }

      &_4 {
        left: 0;
        bottom: 32px;

        width: 200px;

        rotate: -7deg;
      }
    }

    &__hint {
      position: absolute;
      z-index: 10;

      text-shadow: -4px 2px 8px rgba(0, 0, 0, 0.25);

      font-family: var(--font-family-main);
      font-size: 64px;
      font-weight: 900;

      color: #e43233;

      @media (max-width: $lg) {
        position: static;

        transform: none !important;
      }

      &_1 {
        left: -40px;
        top: 64px;

        rotate: -5deg;
        z-index: 1;
      }

      &_2 {
        left: 210px;
        top: 32px;

        rotate: 5deg;
      }

      &_3 {
        right: 210px;
        top: 50%;

        rotate: 5deg;
        translate: 0 -50%;
      }

      &_4 {
        left: -40px;
        bottom: 32px;

        rotate: -7deg;
      }
    }
  }
</style>
